import { motion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'

export interface CaptureData {
  name: string
  email: string
  whatsapp: string
}

interface Props {
  onSubmit: (data: CaptureData) => void
  initial?: CaptureData
}

type CountryKey = 'BR' | 'PT' | 'US' | 'OTHER'

const COUNTRIES: { key: CountryKey; label: string; flag: string; code: string }[] = [
  { key: 'BR', label: 'Brasil', flag: '🇧🇷', code: '+55' },
  { key: 'PT', label: 'Portugal', flag: '🇵🇹', code: '+351' },
  { key: 'US', label: 'Estados Unidos', flag: '🇺🇸', code: '+1' },
  { key: 'OTHER', label: 'Outro país', flag: '🌐', code: '' },
]

function parseInitialWhatsapp(v?: string): { country: CountryKey; customCode: string; number: string } {
  if (!v) return { country: 'BR', customCode: '', number: '' }
  const raw = v.trim()
  for (const c of COUNTRIES) {
    if (c.code && raw.startsWith(c.code)) {
      return { country: c.key, customCode: '', number: raw.slice(c.code.length).trim() }
    }
  }
  if (raw.startsWith('+')) {
    const m = raw.match(/^(\+\d{1,4})\s*(.*)$/)
    if (m) return { country: 'OTHER', customCode: m[1], number: m[2] }
  }
  return { country: 'BR', customCode: '', number: raw }
}

function StepCapture({ onSubmit, initial }: Props) {
  const parsed = parseInitialWhatsapp(initial?.whatsapp)
  const [name, setName] = useState(initial?.name ?? '')
  const [email, setEmail] = useState(initial?.email ?? '')
  const [country, setCountry] = useState<CountryKey>(parsed.country)
  const [customCode, setCustomCode] = useState(parsed.customCode)
  const [whatsapp, setWhatsapp] = useState(parsed.number)
  const [errors, setErrors] = useState<{ name?: string; email?: string; whatsapp?: string }>({})
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const activeCountry = COUNTRIES.find((c) => c.key === country)!
  const effectiveCode = country === 'OTHER' ? customCode.trim() : activeCountry.code

  const numberDigits = whatsapp.replace(/\D/g, '')
  const minDigits = country === 'BR' ? 10 : 7
  const customCodeValid =
    country !== 'OTHER' || /^\+\d{1,4}$/.test(customCode.trim())

  const isValid =
    name.trim().length >= 2 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) &&
    numberDigits.length >= minDigits &&
    customCodeValid

  const formatBrazil = (v: string) => {
    const d = v.replace(/\D/g, '').slice(0, 11)
    if (d.length <= 2) return d
    if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
  }

  const formatInternational = (v: string) => v.replace(/\D/g, '').slice(0, 20)

  const handleWhatsappChange = (v: string) => {
    if (country === 'BR') setWhatsapp(formatBrazil(v))
    else setWhatsapp(formatInternational(v))
  }

  const handleCountryChange = (next: CountryKey) => {
    setCountry(next)
    // Re-format the existing number for the new country rules
    if (next === 'BR') setWhatsapp((prev) => formatBrazil(prev))
    else setWhatsapp((prev) => formatInternational(prev))
  }

  const handleCustomCodeChange = (v: string) => {
    let clean = v.replace(/[^\d+]/g, '')
    if (!clean.startsWith('+')) clean = '+' + clean.replace(/\+/g, '')
    clean = '+' + clean.slice(1).replace(/\+/g, '').slice(0, 4)
    setCustomCode(clean)
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: { name?: string; email?: string; whatsapp?: string } = {}

    if (!name.trim()) {
      newErrors.name = 'Este campo é obrigatório. Por favor informe seu nome.'
    }
    if (!email.trim()) {
      newErrors.email = 'Este campo é obrigatório. Por favor informe seu email.'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = 'Por favor informe um email válido.'
    }
    if (numberDigits.length < minDigits || (country === 'OTHER' && !customCodeValid)) {
      newErrors.whatsapp = 'Este campo é obrigatório. Por favor informe seu WhatsApp.'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }
    setErrors({})
    const fullWhatsapp = `${effectiveCode} ${whatsapp.trim()}`.trim()
    onSubmit({ name: name.trim(), email: email.trim(), whatsapp: fullWhatsapp })
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="w-full max-w-xl mx-auto"
    >
      {/* Ornament */}
      <div className="flex items-center justify-center gap-3 mb-8">
        <span className="h-px w-10 bg-[var(--line)]" />
        <span className="text-[10px] tracking-[0.35em] uppercase text-[var(--ink-soft)] font-body">
          Uma jornada
        </span>
        <span className="h-px w-10 bg-[var(--line)]" />
      </div>

      <h1 className="font-display type-hero text-center text-[var(--ink)] mb-5">
        Reative os <em className="italic font-light"><span className="numerals">4</span> Elementos</em>
      </h1>

      <p className="text-center text-[var(--ink)] type-section font-body font-light mb-5 max-w-lg mx-auto">
        Descubra o que está bloqueando sua vida e como dar o primeiro passo para a mudança.
      </p>

      <p className="text-center text-[var(--ink-soft)] type-body font-body mb-10 max-w-md mx-auto">
        Esta é uma jornada gratuita e personalizada. Em menos de <span className="numerals">3</span> minutos você vai descobrir qual elemento está bloqueando sua prosperidade, seus relacionamentos e seu merecimento, e receber sua Trindade de Cura criada especificamente para você.
      </p>

      {/* Email promise block */}
      <div
        className="flex items-start gap-3 mb-10 px-4 py-4 rounded-sm font-body"
        style={{
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.10)',
        }}
      >
        <span
          className="flex-shrink-0 mt-0.5 text-[var(--ink-soft)]"
          style={{ fontSize: '18px', lineHeight: 1 }}
          aria-hidden="true"
        >
          ✦
        </span>
        <p className="type-body text-[var(--ink-soft)] italic leading-relaxed">
          Ao final desta jornada você vai receber no seu email uma cópia completa da sua Transformação do Campo com seu diagnóstico, sua Trindade de Cura e um presente especial.
        </p>
      </div>

      <div className="divider-thin mb-10" />

      <form onSubmit={submit} className="space-y-5">
        <div>
          <label className="block type-label tracking-[0.25em] uppercase text-[var(--ink-soft)] mb-2 font-body">
            Nome completo
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); if (errors.name) setErrors((prev) => ({ ...prev, name: undefined })) }}
            placeholder="Como você se chama"
            autoComplete="name"
            style={{ backgroundColor: 'transparent' }}
            className={`input-field w-full px-4 py-4 text-[16px] font-body rounded-sm ${name.trim() ? 'is-filled' : ''} ${errors.name ? 'has-error' : ''}`}
          />
          {errors.name ? <span className="field-error">{errors.name}</span> : null}
        </div>

        <div>
          <label className="block type-label tracking-[0.25em] uppercase text-[var(--ink-soft)] mb-2 font-body">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors((prev) => ({ ...prev, email: undefined })) }}
            placeholder="seu@email.com"
            autoComplete="email"
            style={{ backgroundColor: 'transparent' }}
            className={`input-field w-full px-4 py-4 text-[16px] font-body rounded-sm ${email.trim() ? 'is-filled' : ''} ${errors.email ? 'has-error' : ''}`}
          />
          {errors.email ? <span className="field-error">{errors.email}</span> : null}
        </div>

        <div>
          <label className="block type-label tracking-[0.25em] uppercase text-[var(--ink-soft)] mb-2 font-body">
            WhatsApp com DDD
          </label>

          {/* Unified WhatsApp field: compact country selector + number input */}
          <div className={`relative flex items-stretch border rounded-sm transition-colors ${whatsapp.trim() ? 'border-[rgba(255,255,255,0.25)] bg-[rgba(255,255,255,0.08)]' : errors.whatsapp ? 'border-[rgba(220,80,80,0.5)] bg-[rgba(220,80,80,0.04)]' : 'border-[rgba(255,255,255,0.15)] bg-[rgba(255,255,255,0.05)]'} focus-within:border-[rgba(255,255,255,0.4)] focus-within:shadow-[0_0_0_2px_rgba(255,255,255,0.08)]`} ref={dropdownRef}>
            {/* Compact country selector (flag + code only) */}
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-haspopup="listbox"
              aria-expanded={open}
              className="flex items-center gap-1.5 px-3 py-4 text-[15px] font-body text-[var(--ink-soft)] bg-[var(--ink)]/[0.03] border-r border-[var(--line)] hover:text-[var(--ink)] transition-colors flex-shrink-0"
            >
              <span className="text-[15px] leading-none">{activeCountry.flag}</span>
              <span className="tracking-wider numerals text-[13px]">
                {effectiveCode || '+—'}
              </span>
              <svg
                className={`w-3 h-3 text-[var(--ink-soft)] transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M3 4.5L6 7.5L9 4.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {/* Number input */}
            <input
              type="tel"
              inputMode="numeric"
              value={whatsapp}
              onChange={(e) => { handleWhatsappChange(e.target.value); if (errors.whatsapp) setErrors((prev) => ({ ...prev, whatsapp: undefined })) }}
              placeholder="número com DDD"
              className="flex-1 min-w-0 bg-transparent px-3 sm:px-4 py-4 text-[16px] font-body outline-none text-[rgba(255,255,255,0.9)] placeholder:text-[var(--ink-soft)]"
            />

            {/* Compact dropdown menu */}
            {open ? (
              <div
                role="listbox"
                className="absolute left-0 top-full mt-1 w-full sm:w-[280px] overflow-hidden"
                style={{
                  backgroundColor: '#1a1a24',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '8px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                  zIndex: 9999,
                }}
              >
                {COUNTRIES.map((c) => {
                  const active = country === c.key
                  return (
                    <button
                      key={c.key}
                      type="button"
                      role="option"
                      aria-selected={active}
                      onClick={() => {
                        handleCountryChange(c.key)
                        setOpen(false)
                      }}
                      onMouseEnter={(e) => {
                        if (!active) {
                          e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'
                          e.currentTarget.style.color = 'rgba(255,255,255,1)'
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) {
                          e.currentTarget.style.backgroundColor = 'transparent'
                          e.currentTarget.style.color = 'rgba(255,255,255,0.85)'
                        }
                      }}
                      className="w-full flex items-center gap-2.5 text-left font-body transition-colors"
                      style={{
                        padding: '10px 16px',
                        fontSize: '14px',
                        lineHeight: 1.6,
                        color: active ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.85)',
                        backgroundColor: active ? 'rgba(255,255,255,0.12)' : 'transparent',
                      }}
                    >
                      <span className="leading-none flex-shrink-0" style={{ fontSize: '16px' }}>{c.flag}</span>
                      {c.code ? (
                        <span className="numerals tracking-wider flex-shrink-0 min-w-[44px]">
                          {c.code}
                        </span>
                      ) : (
                        <span className="tracking-wider flex-shrink-0 min-w-[44px]">—</span>
                      )}
                      <span className="truncate">{c.label}</span>
                    </button>
                  )
                })}
              </div>
            ) : null}
          </div>

          {errors.whatsapp ? <span className="field-error mt-1">{errors.whatsapp}</span> : null}

          {/* Custom country code input (only for OTHER) */}
          {country === 'OTHER' ? (
            <input
              type="text"
              inputMode="tel"
              value={customCode}
              onChange={(e) => handleCustomCodeChange(e.target.value)}
              placeholder="+00"
              className={`input-field w-full px-4 py-3 text-[15px] font-body rounded-sm mt-2 ${customCode.trim() ? 'is-filled' : ''}`}
            />
          ) : null}
        </div>

        <button
          type="submit"
          disabled={false}
          className="btn-primary w-full py-5 min-h-[52px] type-button rounded-sm tracking-[0.08em] uppercase mt-2"
        >
          Começar minha jornada
        </button>
      </form>
    </motion.div>
  )
}

export default StepCapture
